import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { useAuthStore, useSubscriptionStore } from '@shared/state';
import { handleCheckoutReturn } from '@shared/services/subscription';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants/analytics-events';
import { Button } from '../components/ui/Button';

export function CheckoutSuccessPage() {
  const user = useAuthStore((s) => s.user);
  const setSubscription = useSubscriptionStore((s) => s.setSubscription);
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  const [tierName, setTierName] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    handleCheckoutReturn(user.uid).then((sub) => {
      setSubscription(sub);
      setTierName(sub.tier.charAt(0).toUpperCase() + sub.tier.slice(1));
      setConfirmed(true);

      trackEvent({
        name: ANALYTICS_EVENTS.SUBSCRIPTION_UPGRADE_COMPLETED,
        properties: {
          ...getBaseProperties(),
          tier: sub.tier,
          fromTier: 'free',
          toTier: sub.tier,
          paymentMethod: 'lemon_squeezy',
          revenue: sub.tier === 'learner' ? 9.99 : sub.tier === 'pro' ? 27.99 : 19.99,
        },
      });
    });
  }, [user, navigate, setSubscription]);

  // Particle burst animation for successful payment
  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    angle: (i / 12) * 360,
    delay: Math.random() * 0.3,
    color: i % 2 === 0 ? '#6C5CE7' : '#F0C674',
  }));

  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {!confirmed ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="w-12 h-12 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-content-secondary font-body">Confirming your subscription...</p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            {/* Particle burst */}
            <div className="relative w-24 h-24 mx-auto mb-6">
              {particles.map((p) => (
                <motion.div
                  key={p.id}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: p.color,
                    left: '50%',
                    top: '50%',
                  }}
                  initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                  animate={{
                    x: Math.cos((p.angle * Math.PI) / 180) * 48,
                    y: Math.sin((p.angle * Math.PI) / 180) * 48,
                    opacity: 0,
                    scale: 0,
                  }}
                  transition={{
                    duration: 0.8,
                    delay: p.delay,
                    ease: 'easeOut',
                  }}
                />
              ))}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              >
                <CheckCircle size={64} className="text-success mx-auto" />
              </motion.div>
            </div>

            <motion.h1
              className="font-heading font-bold text-2xl sm:text-3xl mb-3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Welcome to CogniPrint {tierName}!
            </motion.h1>

            <motion.p
              className="text-content-secondary font-body mb-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Your subscription is active. All 8 dimensions of your CogniPrint profile are now unlocked.
            </motion.p>

            <motion.div
              className="space-y-3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Link to="/dashboard" className="block">
                <Button className="w-full" size="lg">
                  Go to Dashboard <ArrowRight size={16} />
                </Button>
              </Link>

              <Link to="/diagnostic" className="block">
                <Button variant="secondary" className="w-full">
                  <Sparkles size={16} /> Take Full Diagnostic
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
